import React from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  ShieldExclamationIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { TabNavigation } from "../ui";
import CRMModuleHeader from "../ui/CRMModuleHeader";

import VaptDashboard from "./pages/VaptDashboard";
import VaptProjects from "./pages/VaptProjects";
import VaptProjectDetail from "./pages/VaptProjectDetail";
import VaptScans from "./pages/VaptScans";
import VaptCreateScan from "./pages/VaptCreateScan";
import VaptScanDetail from "./pages/VaptScanDetail";
import VaptFindings from "./pages/VaptFindings";
import VaptFindingDetail from "./pages/VaptFindingDetail";
import VaptRisk from "./pages/VaptRisk";
import VaptRemediation from "./pages/VaptRemediation";
import VaptReports from "./pages/VaptReports";
import VaptNotifications from "./pages/VaptNotifications";

const NAV_ITEMS = [
  { id: "dashboard", name: "Dashboard", icon: ChartBarIcon, path: "dashboard" },
  { id: "projects", name: "Projects", icon: BuildingOfficeIcon, path: "projects" },
  { id: "scans", name: "Scans", icon: PlayIcon, path: "scans" },
  { id: "findings", name: "Findings", icon: ExclamationTriangleIcon, path: "findings" },
  { id: "risk", name: "Risk", icon: ScaleIcon, path: "risk" },
  { id: "remediation", name: "Remediation", icon: WrenchScrewdriverIcon, path: "remediation" },
  { id: "reports", name: "Reports", icon: DocumentTextIcon, path: "reports" },
  { id: "notifications", name: "Notifications", icon: BellIcon, path: "notifications" },
];

// VAPT module entry point. Owns its own nested <Routes> so App.js only
// needs a single `/ai-vapt/*` route pointing here.
const VaptModule = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeSection = NAV_ITEMS.find((item) =>
    location.pathname.includes(`/ai-vapt/${item.path}`)
  )?.id || "dashboard";

  const goTo = (id, subPath) => {
    navigate(`/ai-vapt/${id}${subPath ? `/${subPath}` : ""}`);
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-full mx-auto  dark:bg-[#0f172a] min-h-screen">
      <CRMModuleHeader
        icon={ShieldExclamationIcon}
        title="AI VAPT"
        description="AI-powered Vulnerability Assessment & Penetration Testing"
      />
      <TabNavigation
        tabs={NAV_ITEMS.map((n) => ({ id: n.id, name: n.name, icon: n.icon }))}
        activeTab={activeSection}
        onTabChange={(id) => goTo(id)}
        boxedActive
      />

      <Routes>
        {/* Absolute targets: a relative <Navigate to="dashboard"> inside the
            splat route resolves against the unmatched URL and re-appends
            "/dashboard" forever (infinite redirect loop). */}
        <Route index element={<Navigate to="/ai-vapt/dashboard" replace />} />
        <Route path="dashboard" element={<VaptDashboard onNavigate={goTo} />} />

        <Route path="projects" element={<VaptProjects />} />
        <Route path="projects/:projectId" element={<VaptProjectDetail />} />

        <Route path="scans" element={<VaptScans />} />
        <Route path="scans/new" element={<VaptCreateScan />} />
        <Route path="scans/:scanId" element={<VaptScanDetail />} />

        <Route path="findings" element={<VaptFindings />} />
        <Route path="findings/:findingId" element={<VaptFindingDetail />} />

        <Route path="risk" element={<VaptRisk />} />
        <Route path="remediation" element={<VaptRemediation />} />
        <Route path="reports" element={<VaptReports />} />
        <Route path="notifications" element={<VaptNotifications />} />

        <Route path="*" element={<Navigate to="/ai-vapt/dashboard" replace />} />
      </Routes>
    </div>
  );
};

export default VaptModule;
